'use client';

import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import { ALWAYS_VALID_NUKI_ID } from '@/utils/constants';
import { useCartStore } from '@/ui/store/cart';
import { NukiIdValidationResult } from '@/ui/utils/nuki-id-validation';

export interface UseNukiIdEntryOptions {
  nukiIds: string[];
  setNukiIds: React.Dispatch<React.SetStateAction<string[]>>;
  /** SKU of the service variant the IDs are collected for. */
  sku: string;
  apiPath: string;
  isValidId: (id: string, apiPath: string) => Promise<NukiIdValidationResult>;
  allowMultiple?: boolean;
  /** Turns an error code from the validation into a message; without it the code is translated. */
  getValidationErrorMessage?: (error: string) => string;
}

export interface NukiIdEntryState {
  loading: boolean;
  validationError?: string;
  handleApplyNukiId: (id: string) => Promise<boolean>;
  handleRemoveNukiId: (id: string) => void;
}

/**
 * Collects the Smart Lock IDs a service is bought for. Every surface that
 * sells a service per ID (the drawer of the product page, the modal of the
 * classic layout) runs the same checks in the same order, so they share them.
 */
export function useNukiIdEntry({
  nukiIds,
  setNukiIds,
  sku,
  apiPath,
  isValidId,
  allowMultiple = true,
  getValidationErrorMessage,
}: UseNukiIdEntryOptions): NukiIdEntryState {
  const t = useTranslations('NukiIdOverlay');
  const [loading, setLoading] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);
  const { isThereAnItemLinkedWithID } = useCartStore();

  const translateValidationError = (error: string): string =>
    (getValidationErrorMessage ? getValidationErrorMessage(error) : t(error));

  const handleApplyNukiId = async (id: string): Promise<boolean> => {
    setValidationError(undefined);
    const alreadyAddedID = nukiIds.find((addedId) => addedId === id);

    if (alreadyAddedID) {
      return true;
    }

    if (!allowMultiple && nukiIds.length > 0) {
      setValidationError(t('Only one ID is allowed'));
      return false;
    }

    if (isThereAnItemLinkedWithID(id, sku)) {
      setValidationError(t('Already present in the cart'));
      return false;
    }

    if (id === ALWAYS_VALID_NUKI_ID) {
      setNukiIds([...nukiIds, id]);
      return true;
    }

    try {
      setLoading(true);
      const { valid, error } = await isValidId(id, apiPath);
      if (valid) {
        const updatedIds = [...nukiIds, id];
        setNukiIds(updatedIds);
        return true;
      }
      setValidationError(error ? translateValidationError(error) : t('Unknown Error has occurred'));
    } catch (error) {
      console.error(error);
      setValidationError(t('Unknown Error has occurred'));
    } finally {
      setLoading(false);
    }
    return false;
  };

  const handleRemoveNukiId = (id: string) => {
    const updatedIds = nukiIds.filter((nukiId) => nukiId !== id);
    setNukiIds(updatedIds);
  };

  return {
    loading,
    validationError,
    handleApplyNukiId,
    handleRemoveNukiId,
  };
}
